import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apartmentRedux } from '../projects/apartment/ui/state/state';
import { IApartment } from '../projects/apartment/data/entities';
import { IAppReduxState } from '../../redux/store';
import { IAsyncData } from '../../core/models';
import { isPending } from '../../core/redux';
import { IImage } from '../../core/image';
import { ProjectPlan } from './types';
import backdropImg from '../../../assets/images/sale-announcement/apartment-backdrop-4f466c.png';
import { Compass } from '../../components/compass';
import { PlanIcon } from '../projects/apartment/ui/images/plan_icon';

interface IProps {
  apartmentId: string;
  activePlan?: ProjectPlan;
  projectImage?: IImage; // kept for API compat; backdrop now uses static Figma image
  onBack: () => void;
}

export const ApartmentDetailInline: React.FC<IProps> = ({
  apartmentId,
  activePlan,
  projectImage,
  onBack,
}) => {
  const dispatch = useDispatch();

  /* Fire the same fetch as the standalone ApartmentDetailPage —
     populates `s.apartment.details` from `/getApartment/:id`. */
  useEffect(() => {
    dispatch(apartmentRedux.actions.getDetail(apartmentId));
  }, [dispatch, apartmentId]);

  const branch = useSelector<IAppReduxState, IAsyncData<IApartment>>(
    (s) => s.apartment.details,
  );
  const loading = isPending(branch);
  const apt = branch.data;

  /* Price formatter. We show only the regular `price` — the
     `discountPrice` field on the apartment is intentionally not
     surfaced in this view. */
  const fmt = (v: number | string | null | undefined) =>
    v == null || v === '' ? '—' : String(v);

  return (
    <div className="asp asp--detail">
      <aside className="asp__form">
        <h3 className="asp__form-title">{activePlan?.name_az ?? ''}</h3>

        {loading && !apt && (
          <div className="adi__loading">Yüklənir…</div>
        )}

        {apt && (
          <>
            <div className="msp__summary">
              <div className="msp__summary-cell">
                <span className="msp__summary-label">Bina</span>
                <span className="msp__summary-value">{apt.buildingNumber ?? '—'}</span>
              </div>
              <span className="msp__summary-divider" aria-hidden />
              <div className="msp__summary-cell">
                <span className="msp__summary-label">Giriş</span>
                <span className="msp__summary-value">{apt.entranceNumber ?? '—'}</span>
              </div>
              <span className="msp__summary-divider" aria-hidden />
              <div className="msp__summary-cell">
                <span className="msp__summary-label">Mərtəbə</span>
                <span className="msp__summary-value">{apt.floorNumber ?? '—'}</span>
              </div>
              <span className="msp__summary-divider" aria-hidden />
              <div className="msp__summary-cell">
                <span className="msp__summary-label">Mənzil</span>
                <span className="msp__summary-value">{apt.apartmentNumber ?? '—'}</span>
              </div>
            </div>

            <div className="msp__spec">
              <div className="msp__spec-list">
                <div className="msp__spec-row">
                  <span className="msp__spec-label">Otaq sayı</span>
                  <span className="msp__spec-value">
                    {apt.roomCount === 0 ? 'Studio' : apt.roomCount}
                  </span>
                </div>
                <div className="msp__spec-row">
                  <span className="msp__spec-label">Xarici perimetr üzrə sahə</span>
                  <span className="msp__spec-value">{apt.externalSize ?? '—'} m²</span>
                </div>
                <div className="msp__spec-row">
                  <span className="msp__spec-label">Daxili perimetr üzrə sahə
                    <br/> <small>(Dövlət qeydiyyat sənədi üzrə)</small>
                  </span>
                  <span className="msp__spec-value">{apt.internalSize ?? '—'} m²</span>
                </div>
              </div>

              {apt.price != null && (
                <div className="msp__spec-price">
                  <span className="msp__spec-price-label">Mənzilin satış qiyməti</span>
                  <span className="msp__spec-price-value">
                    {fmt(apt.price)} AZN
                  </span>
                </div>
              )}

              {apt.fileUrl && (
                <a
                  className="adi__plan-link"
                  href={apt.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PlanIcon className="adi__plan-link-icon" />
                  <span className="adi__plan-link-text">Mənzil planı</span>
                </a>
              )}
            </div>
          </>
        )}

        <div className="adi__actions">
          <button
            type="button"
            className="asp-btn asp-btn--ghost"
            onClick={onBack}
          >
            Geri
          </button>
        </div>
      </aside>

      <div className="asp__panel asp__panel--detail">
        {loading && !apt?.image?.url && (
          <div className="asp__loading">
            <div className="asp__spinner" />
          </div>
        )}

        {apt?.image?.url ? (
          /* Done 14: the plan sits on a blurred, darkened copy of the
             PROJECT image (so the detail view stays anchored to "this
             apartment in *that* project"), with a frosted "Mənzil
             haqqında məlumat" panel on the right listing the
             room-by-room area breakdown. Falls back to the apartment
             image if the project image isn't loaded yet. */
          <div className="adi__stage">
            <div
              className="adi__backdrop"
              style={{
                backgroundImage: `url("${backdropImg}")`,
              }}
              aria-hidden
            />
            <div
              className="adi__plan"
              style={{
                aspectRatio: `${apt.image.width || 4} / ${apt.image.height || 3}`,
              }}
            >
              <img src={apt.image.url} alt="" className="adi__plan-img" />
            </div>

            {apt.areas && apt.areas.length > 0 && (
              <aside className="adi__info" aria-label="Mənzil haqqında məlumat">
                <h4 className="adi__info-title">Mənzil haqqında məlumat</h4>
                <ul className="adi__info-list">
                  {apt.areas.map((a, i) => (
                    <li key={a.id} className="adi__info-row">
                      <span className="adi__info-label">
                        {i + 1}. {a.name?.az ?? '—'}
                      </span>
                      <span className="adi__info-value">{a.area} m²</span>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            {/* Compass — same rotation formula as the Layihələr
                apartment detail page. */}
            {apt.compass != null && (
              <div className="adi__compass">
                <Compass compass={apt.compass} />
              </div>
            )}
          </div>
        ) : (
          !loading && (
            <p className="asp__empty-text">Plan tapılmadı</p>
          )
        )}
      </div>
    </div>
  );
};

export default ApartmentDetailInline;
